import useAppToast from '@/hooks/useAppToast';
import { apiClient } from '@/shared/lib/axios';
import { getApiErrorMessage } from '@/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

interface ImportStatusResponse {
  data: {
    job_id: number;
    status: 'processing' | 'completed' | 'failed';
    total_rows: number;
    imported_count: number;
    errors: { row: number; field: string; message: string }[] | null;
    error_message: string | null;
  };
}

export const importTownsCsv = async (
  siteType: string,
  file: File
): Promise<{ job_id: number; status: string }> => {
  const formData = new FormData();
  formData.append('csv_file', file);

  const response = await apiClient.post(`/admin/${siteType}/towns/import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data?.data;
};

export const getImportStatus = async (
  siteType: string,
  jobId: number
): Promise<ImportStatusResponse['data']> => {
  const response = await apiClient.get<ImportStatusResponse>(
    `/admin/${siteType}/towns/import-status/${jobId}`
  );
  return response.data?.data;
};

export interface ImportResult {
  status: 'idle' | 'uploading' | 'processing' | 'completed' | 'failed';
  errors?: { row: number; field: string; message: string }[];
  errorMessage?: string;
  importedCount?: number;
}

export const useImportTownsCsv = () => {
  const { site } = useParams<{ site: string }>();
  const currentSite = site ?? 'medix';
  const toast = useAppToast();
  const queryClient = useQueryClient();

  const [importResult, setImportResult] = useState<ImportResult>({ status: 'idle' });
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const startPolling = useCallback(
    (jobId: number) => {
      setImportResult({ status: 'processing' });

      pollingRef.current = setInterval(async () => {
        try {
          const result = await getImportStatus(currentSite, jobId);

          if (result.status === 'completed') {
            stopPolling();
            setImportResult({
              status: 'completed',
              importedCount: result.imported_count,
            });
            toast.success('インポートしました。');
            queryClient.invalidateQueries({ queryKey: ['towns'] });
          } else if (result.status === 'failed') {
            stopPolling();
            setImportResult({
              status: 'failed',
              errors: result.errors ?? undefined,
              errorMessage: result.error_message ?? 'エラーが発生しました。',
            });
            toast.error('エラーが発生しました。');
          }
          // status === 'processing' → continue polling
        } catch {
          stopPolling();
          setImportResult({
            status: 'failed',
            errorMessage: 'エラーが発生しました。',
          });
          toast.error('エラーが発生しました。');
        }
      }, 2000);
    },
    [currentSite, stopPolling, toast, queryClient]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => importTownsCsv(currentSite, file),
    onMutate: () => {
      setImportResult({ status: 'uploading' });
    },
    onSuccess: data => {
      startPolling(data.job_id);
    },
    onError: (err: Error) => {
      const message = getApiErrorMessage(err, 'エラーが発生しました。');
      setImportResult({
        status: 'failed',
        errorMessage: message,
      });
      toast.error(message);
    },
  });

  const isImporting = importResult.status === 'uploading' || importResult.status === 'processing';

  const resetImportResult = useCallback(() => {
    setImportResult({ status: 'idle' });
  }, []);

  return {
    importCsv: uploadMutation.mutate,
    isImporting,
    importResult,
    resetImportResult,
  };
};
